import { useState, useRef, useMemo } from 'react';
import { Plus, Trash2, Pencil, Calendar, AlertCircle, RotateCcw, Building2, Download, Upload, ChevronDown, ChevronRight } from 'lucide-react';
import { useRoosterStore } from '../store/useRoosterStore.js';
import { downloadBackup, valideerBackup } from '../lib/backup.js';
import VerlofModal from '../components/VerlofModal.jsx';
import VakantieModal from '../components/VakantieModal.jsx';
import MedewerkerModal from '../components/MedewerkerModal.jsx';
import DienstModal from '../components/DienstModal.jsx';
import LogViewer from '../components/LogViewer.jsx';
import WijzigingViewer from '../components/WijzigingViewer.jsx';
import FteTab from '../components/FteTab.jsx';
import { DAG_KORT, formatDatum } from '../data/afwezigheid.js';
import { ingevuldeUrenVanRecord, TYPEN_MET_UREN } from '../data/verlofuren.js';
import { isoDag, parseDag } from '../lib/tijd.js';
import { bezettingLabel } from '../lib/dienstBezetting.js';
import { isRoosterbaar } from '../lib/rollen.js';
import { maakSleutel } from '../lib/sleutels.js';
import { leesActieveOmgeving } from '../lib/omgeving.js';

const DAG_LABELS = { ma: 'Ma', di: 'Di', wo: 'Wo', do: 'Do', vr: 'Vr', za: 'Za', zo: 'Zo' };
const TYPE_STIJL = {
  verlof: 'bg-blue-100 text-blue-800',
  ziekte: 'bg-red-100 text-red-800',
  anders: 'bg-slate-100 text-slate-700',
};
const DIENST_VOORKEUR_LABELS = {
  dagdienst: 'Dag',
  late_dienst: 'Laat',
  flexibel: 'Flex',
};
const GEMEENTE_KLEUREN = {
  arnhem: 'bg-blue-100 text-blue-700',
  rheden: 'bg-violet-100 text-violet-700',
  lingewaard: 'bg-teal-100 text-teal-700',
};

// ── Medewerkers-tab ──────────────────────────────────────────────────────────

function MedewerkersTab() {
  const { regels, removeMedewerker } = useRoosterStore();
  const [editKey, setEditKey] = useState(null);   // null = gesloten, '' = nieuw, 'emre' = bewerk
  const [zoek, setZoek] = useState('');

  const medewerkers = Object.entries(regels.medewerkers)
    .filter(([k]) => !k.startsWith('_'))
    .filter(([, mw]) => mw.naam.toLowerCase().includes(zoek.toLowerCase()));

  function verwijder(key, naam) {
    if (!window.confirm(`Weet je zeker dat je ${naam} wilt verwijderen?`)) return;
    removeMedewerker(key);
  }

  return (
    <div className="space-y-4">
      <div className="flex gap-3">
        <input
          type="text"
          value={zoek}
          onChange={e => setZoek(e.target.value)}
          placeholder="Zoek medewerker…"
          className="flex-1 min-w-0 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
        />
        <button
          onClick={() => setEditKey('')}
          className="flex items-center gap-2 px-4 py-2 bg-brand-700 text-white rounded-lg text-sm font-semibold hover:bg-brand-800 transition shrink-0"
        >
          <Plus className="w-4 h-4" />
          Nieuwe medewerker
        </button>
      </div>

      <div className="space-y-2">
        {medewerkers.map(([key, mw]) => (
          <MedewerkerKaart
            key={key}
            mwKey={key}
            mw={mw}
            regels={regels}
            onBewerk={() => setEditKey(key)}
            onVerwijder={() => verwijder(key, mw.naam)}
          />
        ))}
        {medewerkers.length === 0 && (
          <div className="text-center py-8 text-slate-400 text-sm">
            Geen medewerkers gevonden.
          </div>
        )}
      </div>

      {editKey !== null && (
        <MedewerkerModal
          mwKey={editKey || null}
          onSluit={() => setEditKey(null)}
        />
      )}
    </div>
  );
}

// Valt er in DEZE omgeving iets te verkiezen tussen dag en laat?
//
// De soort-afleiding is LETTERLIJK die van de engine (rooster-engine.js, de twee takken in
// vulSlot): 'dagdienst' is dag, alles wat met 'late_dienst' begint plus 'flexibel' is laat.
// Bewust geen tweede definitie ernaast. Een sleutel die geen van beide is krijgt in de engine
// ook geen dag/laat-behandeling en telt hier dus niet als eigen soort.
//
// OMGEVINGSBREED, niet per gemeente: dienst_voorkeur is ÉÉN waarde op de medewerker, niet
// één per gemeente, en iemand kan in meerdere gemeenten inzetbaar zijn. Een omgeving waarin
// de ene gemeente alleen laat draait en de andere ook dag, heeft dus wél iets te verkiezen.
// Inactieve gemeenten en '_'-meta-sleutels tellen niet mee, net als in sorteerGemeenten.
// Nul gemeenten of nul diensten geeft een lege verzameling en dus false — geen badge, geen fout.
function heeftMeerdereDienstSoorten(regels) {
  const soorten = new Set();
  for (const [gemKey, gem] of Object.entries(regels?.gemeenten ?? {})) {
    if (gemKey.startsWith('_') || gem?.actief === false) continue;
    for (const dienstId of Object.keys(gem?.diensten ?? {})) {
      if (dienstId === 'dagdienst') soorten.add('dag');
      if (dienstId.startsWith('late_dienst') || dienstId === 'flexibel') soorten.add('laat');
    }
  }
  return soorten.size > 1;
}

function MedewerkerKaart({ mw, regels, onBewerk, onVerwijder }) {
  const isMedewerker = isRoosterbaar(mw);
  // Eigenschap van de OMGEVING, niet van deze medewerker: voor elke kaart in de lijst komt
  // hier dezelfde uitkomst uit. Hier berekend en niet in MedewerkersTab, omdat `regels` al
  // binnenkomt en het om een handvol gemeenten gaat; dat scheelt een extra prop.
  const toonDienstVoorkeur = heeftMeerdereDienstSoorten(regels);
  const isTeamleider = Array.isArray(mw.rollen) && mw.rollen.includes('teamleider');
  const gemeenteTags = (mw.gemeenten || []).map(g => ({
    key: g,
    naam: regels.gemeenten[g]?.naam || g,
  }));

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-bold text-lg shrink-0">
            {mw.naam[0]}
          </div>
          <div>
            <div className="font-semibold text-slate-800">{mw.naam}</div>
            {isMedewerker && (
              <div className="text-xs text-slate-500">
                {mw.contract_uren}u/week contract
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <div className="flex gap-1 flex-wrap justify-end">
            {isMedewerker && gemeenteTags.map(g => (
              <span key={g.key} className={`px-2 py-0.5 rounded text-xs font-medium ${GEMEENTE_KLEUREN[g.key] || 'bg-slate-100 text-slate-700'}`}>
                {g.naam}
              </span>
            ))}
            {/* Voorkeursbadge alleen waar de voorkeur ergens over gaat. dienst_voorkeur doet in
                de engine precies twee dingen: het stuurt de VOLGORDE waarin kandidaten voor een
                slot gepakt worden (voorkeurScore in rooster-engine.js), en het weigert
                late-voorkeur medewerkers hard op dagdienst-EXTRA's in fase 2 (de
                weigerLatePrefDag-tak in vulSlot). Allebei zetten ze dag AF TEGEN laat. Kent een
                omgeving maar één dienstsoort, dan valt er niets te ordenen en niets te weigeren:
                het veld doet daar aantoonbaar niets, terwijl de badge "Laat" wél iets lijkt te
                beweren — pal naast een bewerkscherm dat over BESCHIKBAARHEID gaat en "dag+laat"
                toont. Dat leest als een tegenspraak die er niet is. Vandaar: weg waar hij niets
                zegt, ongewijzigd waar hij wel iets zegt. */}
            {isMedewerker && toonDienstVoorkeur && mw.dienst_voorkeur && (
              <span className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-xs font-medium">
                {DIENST_VOORKEUR_LABELS[mw.dienst_voorkeur] || mw.dienst_voorkeur}
              </span>
            )}
            {isTeamleider && (
              <span className="px-2 py-0.5 rounded text-xs font-medium bg-violet-100 text-violet-700">
                Teamleider
              </span>
            )}
          </div>
          <button
            onClick={onBewerk}
            className="p-2 text-slate-400 hover:text-brand-700 hover:bg-brand-50 rounded-lg transition"
            title="Bewerken"
          >
            <Pencil className="w-4 h-4" />
          </button>
          <button
            onClick={onVerwijder}
            className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition"
            title="Verwijderen"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Snel-info (alleen voor ingeroosterde medewerkers) */}
      {isMedewerker && (
      <div className="mt-3 pt-3 border-t border-slate-100 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
        {mw.vaste_vrije_dagen?.length > 0 && (
          <span>Vrij: {mw.vaste_vrije_dagen.map(d => DAG_LABELS[d]).join(', ')}</span>
        )}
        {mw.kantoor_dagen?.length > 0 && (
          <span>Kantoor: {mw.kantoor_dagen.map(d => DAG_LABELS[d]).join(', ')}</span>
        )}
        {mw.school?.dag && (
          <span>School: {DAG_LABELS[mw.school.dag]} ({mw.school.uren}u)</span>
        )}
        {mw.geen_late_dienst_dagen?.length > 0 && (
          <span>Geen laat: {mw.geen_late_dienst_dagen.map(d => DAG_LABELS[d]).join(', ')}</span>
        )}
        {mw.voorkeur_gemeente && !mw.vast_rotatiepatroon && (
          <span>Voorkeur: {regels.gemeenten[mw.voorkeur_gemeente]?.naam || mw.voorkeur_gemeente}</span>
        )}
        {mw.vast_rotatiepatroon && mw.voorkeur_gemeente && (
          <span className="text-teal-700 font-medium">
            Vast patroon: {regels.gemeenten[mw.voorkeur_gemeente]?.naam || mw.voorkeur_gemeente}
          </span>
        )}
        {mw.alleen_dagdienst && <span className="text-sky-600 font-medium">Alleen dagdienst</span>}
        {mw.geen_dagdienst && <span className="text-indigo-600 font-medium">Nooit dagdienst</span>}
        {/* Weekend-inzet. DRIE gevallen, want weekend_rotatie vertelt niet het hele verhaal:
            bij een vast rotatiepatroon komt het weekend uit de dienstcyclus van de voorkeur-
            gemeente (week_a_dagen/week_b_dagen), en is weekend_rotatie in de engine dood.
            Haruun en Myrthe kregen daardoor "Elk weekend" te zien terwijl ze om-het-weekend
            draaien. Bewust NIET "om het weekend" bij een vast patroon: of dat zo is hangt van
            de dienstdefinities af en kan per gemeente verschillen — "volgt X" is altijd waar. */}
        {mw.vast_rotatiepatroon && mw.voorkeur_gemeente
          ? <span className="text-violet-700 font-medium">
              Weekend volgt {regels.gemeenten[mw.voorkeur_gemeente]?.naam || mw.voorkeur_gemeente}
            </span>
          : mw.weekend_rotatie === false
            ? <span className="text-amber-600 font-medium">Elk weekend</span>
            : <span className="text-green-600 font-medium">Om-het-weekend vrij</span>
        }
      </div>
      )}

      {/* Afspraken */}
      {mw.afspraken?.length > 0 && (
        <div className="mt-2 pt-2 border-t border-slate-100 space-y-0.5">
          {mw.afspraken.map((a, i) => (
            <div key={i} className="flex items-center gap-2 text-xs text-slate-500">
              <span className="w-1 h-1 rounded-full bg-slate-300 shrink-0" />
              {a}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Gemeenten-tab ────────────────────────────────────────────────────────────

function GemeentenTab() {
  const { regels, updateGemeente, addGemeente, removeGemeente, removeDienst } = useRoosterStore();
  const kg = regels.instellingen?.koppel_grootte || 2;
  const [editGem, setEditGem]       = useState(null); // { key, naam }
  const [editDienst, setEditDienst] = useState(null); // { gemKey, dienstKey } of { gemKey, dienstKey: null } voor nieuw
  const [nieuweGem, setNieuweGem]   = useState(null); // null = regel dicht, string = ingetypte naam
  const [gemFout, setGemFout]       = useState('');   // zichtbare weigering-melding bij toevoegen

  const gemeenten = Object.entries(regels.gemeenten).filter(([k]) => !k.startsWith('_'));

  function slaGemeenteOp() {
    if (!editGem) return;
    updateGemeente(editGem.key, { naam: editGem.naam });
    setEditGem(null);
  }

  function verwijderDienst(gemKey, dienstKey, label) {
    if (!window.confirm(`Dienst "${label}" verwijderen uit ${regels.gemeenten[gemKey]?.naam}?`)) return;
    removeDienst(gemKey, dienstKey);
  }

  // ── Gemeente toevoegen ──────────────────────────────────────────────────
  // Drie weigeringen, elk met een zichtbare reden:
  //  1. lege sleutel na normalisatie (invoer zonder [a-z0-9_])
  //  2. sleutel begint met '_' — die wordt overal als meta-veld weggefilterd
  //     (de `gemeenten`-filter hierboven, MedewerkerModal.jsx:195) — onzichtbaar dus
  //  3. sleutel bestaat al — addGemeente loopt via updateGemeenteIn en zou de
  //     bestaande gemeente stil muteren i.p.v. een nieuwe aanmaken
  function voegGemeenteToe() {
    const naam = (nieuweGem || '').trim();
    const key  = maakSleutel(naam);
    if (!key) {
      setGemFout('Geef een naam met minstens één letter of cijfer.');
      return;
    }
    if (key.startsWith('_')) {
      setGemFout(`Sleutel '${key}' begint met '_' — dat is gereserveerd voor meta-velden en zou de gemeente onzichtbaar maken.`);
      return;
    }
    if (regels.gemeenten[key]) {
      setGemFout(`Sleutel '${key}' bestaat al (${regels.gemeenten[key].naam || key}). Kies een andere naam.`);
      return;
    }
    addGemeente(key, { naam, diensten: {}, actief: true });
    setNieuweGem(null);
    setGemFout('');
  }

  function sluitNieuweGem() {
    setNieuweGem(null);
    setGemFout('');
  }

  // ── Gemeente verwijderen ────────────────────────────────────────────────
  // Blokkeert zolang er nog iets NAAR deze gemeente wijst. Historie telt bewust
  // NIET mee: oude maandroosters en vastgelegde weken blijven staan en blijven
  // leesbaar (de engine slaat een verweesde gemKey over, de naam valt terug op
  // de kale key) — zelfde gedrag als bij een verwijderde medewerker.
  function blokkades(gemKey, gem) {
    const mws = Object.entries(regels.medewerkers)
      .filter(([k]) => !k.startsWith('_'))
      .map(([, mw]) => mw);
    const nDiensten = Object.keys(gem.diensten || {}).length;
    const nLid      = mws.filter(mw => (mw.gemeenten || []).includes(gemKey)).length;
    const nVoorkeur = mws.filter(mw => mw.voorkeur_gemeente === gemKey).length;
    const nWeekend  = mws.filter(mw => mw.vast_weekend_gemeente === gemKey).length;

    const redenen = [];
    if (nDiensten) redenen.push(`nog ${nDiensten} dienst${nDiensten !== 1 ? 'en' : ''}`);
    if (nLid)      redenen.push(`${nLid} medewerker${nLid !== 1 ? 's' : ''} ingesteld op deze gemeente`);
    if (nVoorkeur) redenen.push(`${nVoorkeur} met deze voorkeur-gemeente`);
    if (nWeekend)  redenen.push(`${nWeekend} met dit vaste weekend`);
    return redenen;
  }

  function verwijderGemeente(gemKey, gem) {
    if (blokkades(gemKey, gem).length > 0) return; // knop is al disabled; tweede grendel
    if (!window.confirm(`Gemeente "${gem.naam || gemKey}" verwijderen?`)) return;
    removeGemeente(gemKey);
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-500">
        Gemeenten in-/uitschakelen, naam aanpassen, en diensten volledig bewerken.
      </p>

      <div className="space-y-3">
        {gemeenten.map(([gemKey, gem]) => {
          const redenen = blokkades(gemKey, gem);
          return (
          <div key={gemKey} className="bg-white rounded-2xl border border-slate-200 p-5">
            {/* Gemeente-header */}
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                  gem.actief !== false ? 'bg-green-100' : 'bg-slate-100'
                }`}>
                  <Building2 className={`w-5 h-5 ${gem.actief !== false ? 'text-green-600' : 'text-slate-400'}`} />
                </div>
                <div>
                  {editGem?.key === gemKey ? (
                    <input
                      type="text"
                      value={editGem.naam}
                      onChange={e => setEditGem(p => ({ ...p, naam: e.target.value }))}
                      onBlur={slaGemeenteOp}
                      onKeyDown={e => e.key === 'Enter' && slaGemeenteOp()}
                      autoFocus
                      className="font-semibold text-slate-800 border-b border-brand-300 focus:outline-none bg-transparent"
                    />
                  ) : (
                    <div
                      className="font-semibold text-slate-800 cursor-pointer hover:text-brand-700"
                      onClick={() => setEditGem({ key: gemKey, naam: gem.naam })}
                      title="Klik om naam te bewerken"
                    >
                      {gem.naam}
                    </div>
                  )}
                  <div className="text-xs text-slate-500 mt-0.5">
                    {Object.keys(gem.diensten || {}).length} dienst-type(s)
                    {gem.actief === false && ' · Inactief'}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-3">
                {/* Actief toggle */}
                <div
                  onClick={() => updateGemeente(gemKey, { actief: gem.actief === false })}
                  className={`flex items-center gap-2 cursor-pointer select-none`}
                >
                  <div className={`w-10 h-5 rounded-full transition-colors relative ${
                    gem.actief !== false ? 'bg-green-500' : 'bg-slate-300'
                  }`}>
                    <div className={`w-4 h-4 rounded-full bg-white absolute top-0.5 transition-all ${
                      gem.actief !== false ? 'left-5' : 'left-0.5'
                    }`} />
                  </div>
                  <span className="text-sm text-slate-600">
                    {gem.actief !== false ? 'Actief' : 'Inactief'}
                  </span>
                </div>

                {/* Gemeente verwijderen — geblokkeerd zolang er iets aan hangt */}
                <button
                  onClick={() => verwijderGemeente(gemKey, gem)}
                  disabled={redenen.length > 0}
                  className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition disabled:opacity-30 disabled:hover:text-slate-400 disabled:hover:bg-transparent disabled:cursor-not-allowed"
                  title={redenen.length > 0 ? `Verwijderen kan niet: ${redenen.join(' · ')}` : 'Gemeente verwijderen'}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>

            {/* Reden waarom verwijderen (nog) niet kan — zichtbaar, niet alleen als tooltip */}
            {redenen.length > 0 && (
              <div className="mt-3 flex items-start gap-1.5 text-xs text-slate-500">
                <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-px text-slate-400" />
                <span>Verwijderen kan niet: {redenen.join(' · ')}</span>
              </div>
            )}

            {/* Diensten — bewerkbaar */}
            <div className="mt-4 pt-3 border-t border-slate-100 space-y-2">
              {Object.entries(gem.diensten || {}).map(([dKey, dienst]) => (
                <div key={dKey} className="flex items-center gap-3 bg-slate-50 rounded-xl px-3 py-2">
                  <div className="flex-1 min-w-0">
                    <span className="text-sm font-semibold text-slate-700">{dienst.label || dKey}</span>
                    <span className="text-xs text-slate-400 ml-2">
                      {dienst.tijd_start !== 'flexibel'
                        ? `${dienst.tijd_start}–${dienst.tijd_eind}`
                        : 'flexibel'}
                      {' · '}
                      {bezettingLabel(dienst, kg)}
                      {' · '}
                      {(dienst.dagen || []).map(d => DAG_KORT[d]).join(', ')}
                    </span>
                  </div>
                  <button
                    onClick={() => setEditDienst({ gemKey, dienstKey: dKey })}
                    className="p-1.5 text-slate-400 hover:text-brand-700 hover:bg-brand-50 rounded-lg transition"
                    title="Dienst bewerken"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => verwijderDienst(gemKey, dKey, dienst.label || dKey)}
                    className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition"
                    title="Dienst verwijderen"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}

              <button
                onClick={() => setEditDienst({ gemKey, dienstKey: null })}
                className="flex items-center gap-1.5 text-xs text-brand-700 hover:text-brand-800 font-medium px-3 py-1 rounded-lg hover:bg-brand-50 transition"
              >
                <Plus className="w-3.5 h-3.5" />
                Dienst toevoegen
              </button>
            </div>
          </div>
          );
        })}
      </div>

      {/* Gemeente toevoegen — inline regel, zelfde idioom als "Dienst toevoegen" */}
      {nieuweGem === null ? (
        <button
          onClick={() => { setNieuweGem(''); setGemFout(''); }}
          className="flex items-center gap-1.5 text-xs text-brand-700 hover:text-brand-800 font-medium px-3 py-1 rounded-lg hover:bg-brand-50 transition"
        >
          <Plus className="w-3.5 h-3.5" />
          Gemeente toevoegen
        </button>
      ) : (
        <div className="bg-white rounded-2xl border border-brand-200 p-5 space-y-2">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-slate-100 shrink-0">
              <Building2 className="w-5 h-5 text-slate-400" />
            </div>
            <input
              type="text"
              value={nieuweGem}
              onChange={e => { setNieuweGem(e.target.value); setGemFout(''); }}
              onKeyDown={e => {
                if (e.key === 'Enter') voegGemeenteToe();
                if (e.key === 'Escape') sluitNieuweGem();
              }}
              autoFocus
              placeholder="Naam van de gemeente"
              className="flex-1 min-w-0 font-semibold text-slate-800 border-b border-brand-300 focus:outline-none bg-transparent"
            />
            <button
              onClick={voegGemeenteToe}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white bg-brand-700 hover:bg-brand-800 transition"
            >
              Toevoegen
            </button>
            <button
              onClick={sluitNieuweGem}
              className="px-3 py-1.5 rounded-lg text-xs font-medium bg-slate-100 text-slate-700 hover:bg-slate-200 transition"
            >
              Annuleren
            </button>
          </div>

          <div className="text-xs text-slate-400 pl-13">
            Sleutel: {maakSleutel(nieuweGem.trim()) || '—'} · Enter = opslaan, Esc = annuleren
          </div>

          {gemFout && (
            <div className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 rounded-lg px-3 py-2">
              <AlertCircle className="w-4 h-4 shrink-0" />
              {gemFout}
            </div>
          )}
        </div>
      )}

      {editDienst && (
        <DienstModal
          gemKey={editDienst.gemKey}
          dienstKey={editDienst.dienstKey}
          onSluit={() => setEditDienst(null)}
        />
      )}
    </div>
  );
}

// ── Verlof-tab ───────────────────────────────────────────────────────────────

function periodeLabel(item) {
  if (item.vanDatum && item.totDatum) {
    const van = new Date(item.vanDatum + 'T00:00:00');
    const tot = new Date(item.totDatum + 'T00:00:00');
    return van.getTime() === tot.getTime()
      ? formatDatum(van, true)
      : `${formatDatum(van)} – ${formatDatum(tot, true)}`;
  }
  if (item.dagen) return item.dagen.map(d => DAG_KORT[d]).join(', ');
  return '—';
}

// ── Verlof-groepering: per maand → per medewerker ────────────────────────────

const MAAND_NAMEN = ['januari', 'februari', 'maart', 'april', 'mei', 'juni',
  'juli', 'augustus', 'september', 'oktober', 'november', 'december'];

// parseDag en isoDag stonden hier lokaal; ze zitten nu in lib/tijd.js zodat er één
// definitie is voor het hele project (zie de import bovenaan).

// 'JJJJ-MM' → "juli 2026"
function maandLabel(maandKey) {
  const [jaar, maand] = maandKey.split('-').map(Number);
  return `${MAAND_NAMEN[maand - 1]} ${jaar}`;
}

// Ligt de maand volledig achter ons? new Date(jaar, maand, 0) = laatste dag van die maand.
function maandIsVoorbij(maandKey) {
  const [jaar, maand] = maandKey.split('-').map(Number);
  const vandaag = new Date();
  vandaag.setHours(0, 0, 0, 0);
  return new Date(jaar, maand, 0) < vandaag;
}

function aantalLabel(n, enkelvoud, meervoud) {
  return `${n} ${n === 1 ? enkelvoud : meervoud}`;
}

// 6.4 → "6,4". Nederlandse komma, zelfde notatie als de kantooruren in de matrix
// (Weekrooster.jsx:66). Geen eenheid — die staat in de JSX ernaast.
const urenKort = (n) => String(n).replace('.', ',');

/**
 * Het uren-merkje achter één afwezigheidsregel.
 *
 * Drie standen, en ze zeggen alle drie iets anders:
 *   een ingevuld getal             → toon het (dat is wat de balans gaat gebruiken)
 *   verlof of ziekte zónder getal  → "uren ontbreekt", want DIT record kan bijgewerkt worden
 *   anders                         → niets; dat type draagt per definitie geen uren
 *
 * ingevuldeUrenVanRecord is dezelfde poort die de balans en de engine gebruiken
 * (data/verlofuren.js), dus wat hier als "ingevuld" telt, telt daar ook zo.
 * GEEN MIGRATIE: een oud record zonder veld blijft gewoon geldig en rekent door op
 * 8u per would-be-werkdag — het merkje is een uitnodiging, geen foutmelding.
 */
function UrenMerk({ item }) {
  const uren = ingevuldeUrenVanRecord(item);
  if (uren !== null) {
    return (
      <span className="shrink-0 text-xs font-medium text-slate-600 tabular-nums" title="Totaal voor de hele periode, uit Numbers">
        {urenKort(uren)} u
      </span>
    );
  }
  if (!TYPEN_MET_UREN.includes(item.type)) return null;
  return (
    <span className="shrink-0 text-xs text-amber-600" title="Nog geen urentotaal ingevuld — dit record rekent met 8u per werkdag">
      uren ontbreekt
    </span>
  );
}

/**
 * Verdeelt één periode over de maanden waar hij overlap mee heeft:
 *   { 'JJJJ-MM' → ['JJJJ-MM-DD', ...] } — per maand alleen de dagen die daarin vallen.
 * Dag-voor-dag lopen clampt vanzelf op de maandgrens.
 */
function dagenPerMaand(vanDatum, totDatum) {
  const perMaand = new Map();
  const tot = parseDag(totDatum);
  const cursor = parseDag(vanDatum);
  while (cursor <= tot) {
    const dag = isoDag(cursor);
    const maand = dag.slice(0, 7);
    if (!perMaand.has(maand)) perMaand.set(maand, []);
    perMaand.get(maand).push(dag);
    cursor.setDate(cursor.getDate() + 1);
  }
  return perMaand;
}

/**
 * Groepeert de afwezigheid per maand (oudste boven), daarbinnen per medewerker op naam
 * (alfabetisch). Een periode over een maandgrens verschijnt onder béide maanden, met per
 * maand alleen de eigen dagen. Dagen tellen via een Set, zodat overlappende periodes van
 * dezelfde persoon niet dubbel meetellen.
 *
 * Items zonder datums (oud formaat { dagen: [...] }, zie afwezigheid.js) horen bij geen
 * enkele maand en gaan naar zonderDatum.
 *
 * `zonderUren` telt de verlof- en ziekterecords (TYPEN_MET_UREN) zonder ingevuld urentotaal.
 * Bewust over de platte invoerlijst geteld en niet per maand: een periode over een
 * maandgrens staat onder béide maanden, en zou per maand geteld dus dubbel meetellen. Dit is
 * het getal dat bovenaan de tab komt te staan — "wat kan ik nog bijwerken".
 */
function groepeerAfwezigheid(afwezigheid) {
  const maanden = new Map();   // 'JJJJ-MM' → Map(naam → { naam, items, dagen: Set })
  const zonderDatum = [];
  const zonderUren = afwezigheid.filter(
    (item) => TYPEN_MET_UREN.includes(item.type) && ingevuldeUrenVanRecord(item) === null,
  ).length;

  for (const item of afwezigheid) {
    if (!item.vanDatum || !item.totDatum) {
      zonderDatum.push(item);
      continue;
    }
    for (const [maand, dagen] of dagenPerMaand(item.vanDatum, item.totDatum)) {
      if (!maanden.has(maand)) maanden.set(maand, new Map());
      const perNaam = maanden.get(maand);
      if (!perNaam.has(item.naam)) {
        perNaam.set(item.naam, { naam: item.naam, items: [], dagen: new Set() });
      }
      const groep = perNaam.get(item.naam);
      groep.items.push(item);
      for (const dag of dagen) groep.dagen.add(dag);
    }
  }

  const gesorteerd = [...maanden.entries()]
    .sort(([a], [b]) => a.localeCompare(b))   // 'JJJJ-MM' sorteert als tekst al chronologisch
    .map(([maandKey, perNaam]) => {
      const medewerkers = [...perNaam.values()]
        .sort((a, b) => a.naam.localeCompare(b.naam, 'nl'))
        .map(groep => ({ ...groep, dagen: groep.dagen.size }));
      return {
        maandKey,
        medewerkers,
        totaalDagen: medewerkers.reduce((som, groep) => som + groep.dagen, 0),
      };
    });

  return { maanden: gesorteerd, zonderDatum, zonderUren };
}

function VerlofTab() {
  const { afwezigheid, removeAfwezigheid } = useRoosterStore();
  const [toonVerlofModal, setToonVerlofModal] = useState(false);
  // Alleen expliciete klikken; zonder klik komt de stand uit maandIsVoorbij.
  const [openMaanden, setOpenMaanden] = useState({});

  const { maanden, zonderDatum, zonderUren } = useMemo(() => groepeerAfwezigheid(afwezigheid), [afwezigheid]);

  return (
    <div className="space-y-4">
      {/* Wat er nog bijgewerkt kan worden. Verdwijnt vanzelf zodra elk verlof- en
          ziekterecord een urentotaal heeft; oude records zijn geldig, dus dit is geen
          waarschuwing. */}
      {zonderUren > 0 && (
        <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-xs text-amber-700">
          <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
          <span>
            {aantalLabel(zonderUren, 'verlof- of ziekterecord heeft', 'verlof- en ziekterecords hebben')} nog geen urentotaal
            uit Numbers. Die rekenen met 8 uur per werkdag, zoals altijd. Hieronder staan ze
            gemerkt met <span className="font-medium">uren ontbreekt</span> — verwijder zo'n record
            en voer het opnieuw in om het bij te werken.
          </span>
        </div>
      )}

      <div className="flex justify-end items-center">
        <button
          onClick={() => setToonVerlofModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-brand-700 text-white rounded-lg text-sm font-semibold hover:bg-brand-800 transition"
        >
          <Plus className="w-4 h-4" />
          Toevoegen
        </button>
      </div>

      {afwezigheid.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center text-slate-400">
          <Calendar className="w-10 h-10 mx-auto mb-3 opacity-40" />
          <p>Nog geen verlof of ziekte ingevoerd.</p>
        </div>
      ) : (
        <div className="space-y-5">
          {maanden.map(maand => {
            const open = openMaanden[maand.maandKey] ?? !maandIsVoorbij(maand.maandKey);
            return (
              <section key={maand.maandKey} className="space-y-2">
                <button
                  onClick={() => setOpenMaanden(stand => ({ ...stand, [maand.maandKey]: !open }))}
                  className="w-full flex items-center justify-between gap-3 px-1 py-1 group"
                >
                  <span className="flex items-center gap-1.5 text-sm font-semibold text-slate-600 uppercase tracking-wide group-hover:text-slate-800">
                    {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    {maandLabel(maand.maandKey)}
                  </span>
                  <span className="text-xs text-slate-400">
                    {aantalLabel(maand.medewerkers.length, 'persoon', 'personen')} · {aantalLabel(maand.totaalDagen, 'dag', 'dagen')}
                  </span>
                </button>

                {open && maand.medewerkers.map(groep => (
                  <div key={groep.naam} className="bg-white rounded-xl border border-slate-200 px-5 py-4">
                    <div className="flex items-center justify-between gap-3">
                      <span className="font-semibold text-slate-800">{groep.naam}</span>
                      <span className="text-xs text-slate-400">{aantalLabel(groep.dagen, 'dag', 'dagen')}</span>
                    </div>
                    <div className="mt-1.5 divide-y divide-slate-100">
                      {groep.items.map(item => (
                        <div key={item.id} className="flex items-center gap-3 py-1.5">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium capitalize shrink-0 ${TYPE_STIJL[item.type] || TYPE_STIJL.anders}`}>
                            {item.type}
                          </span>
                          <span className="flex-1 text-sm text-slate-500">
                            {periodeLabel(item)}
                            {item.omschrijving && ` — ${item.omschrijving}`}
                          </span>
                          <UrenMerk item={item} />
                          <button
                            onClick={() => removeAfwezigheid(item.id)}
                            className="text-slate-300 hover:text-red-500 transition p-1 shrink-0"
                            title="Verwijderen"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </section>
            );
          })}

          {zonderDatum.length > 0 && (
            <section className="space-y-2">
              <div className="px-1 py-1 text-sm font-semibold text-slate-600 uppercase tracking-wide">
                Zonder datum
              </div>
              {zonderDatum.map(item => (
                <div key={item.id} className="bg-white rounded-xl border border-slate-200 px-5 py-4 flex items-center gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-slate-800">{item.naam}</span>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium capitalize ${TYPE_STIJL[item.type] || TYPE_STIJL.anders}`}>
                        {item.type}
                      </span>
                    </div>
                    <div className="text-sm text-slate-500 mt-0.5 flex items-center gap-2">
                      <span>
                        {periodeLabel(item)}
                        {item.omschrijving && ` — ${item.omschrijving}`}
                      </span>
                      <UrenMerk item={item} />
                    </div>
                  </div>
                  <button
                    onClick={() => removeAfwezigheid(item.id)}
                    className="text-slate-300 hover:text-red-500 transition p-1"
                    title="Verwijderen"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </section>
          )}
        </div>
      )}

      <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-xs text-amber-700">
        <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
        <span>
          Verlof en ziekte staan meteen in de matrix en het teamrooster. Genereer het maandrooster
          opnieuw als je wilt dat het programma de nog niet vastgelegde weken opnieuw verdeelt.
        </span>
      </div>

      {toonVerlofModal && <VerlofModal onSluit={() => setToonVerlofModal(false)} />}
    </div>
  );
}

// ── Vakanties-tab ────────────────────────────────────────────────────────────

function VakantiesTab() {
  const { vakanties, removeVakantie } = useRoosterStore();
  // undefined = modal dicht; null = nieuwe toevoegen; object = bewerken.
  const [bewerkVakantie, setBewerkVakantie] = useState(undefined);

  const gesorteerd = [...vakanties].sort((a, b) => a.vanDatum.localeCompare(b.vanDatum));

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-slate-500">
          Schoolvakanties (regio Zuid). Wettelijk vastgestelde periodes zijn gemarkeerd.
        </p>
        <button
          onClick={() => setBewerkVakantie(null)}
          className="flex items-center gap-2 px-4 py-2 bg-brand-700 text-white rounded-lg text-sm font-semibold hover:bg-brand-800 transition"
        >
          <Plus className="w-4 h-4" />
          Vakantie toevoegen
        </button>
      </div>

      {gesorteerd.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center text-slate-400">
          <Calendar className="w-10 h-10 mx-auto mb-3 opacity-40" />
          <p>Nog geen vakanties ingevoerd.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {gesorteerd.map(vakantie => (
            <div key={vakantie.id} className="bg-white rounded-xl border border-slate-200 px-5 py-4 flex items-center gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-slate-800">{vakantie.naam}</span>
                  {vakantie.vast && (
                    <span className="text-xs bg-slate-100 text-slate-500 rounded px-2 py-0.5">wettelijk</span>
                  )}
                </div>
                <div className="text-sm text-slate-500 mt-0.5">{periodeLabel(vakantie)}</div>
              </div>
              <button
                onClick={() => setBewerkVakantie(vakantie)}
                className="text-slate-300 hover:text-brand-600 transition p-1"
                title="Bewerken"
              >
                <Pencil className="w-4 h-4" />
              </button>
              <button
                onClick={() => removeVakantie(vakantie.id)}
                className="text-slate-300 hover:text-red-500 transition p-1"
                title="Verwijderen"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {bewerkVakantie !== undefined && (
        <VakantieModal vakantie={bewerkVakantie} onSluit={() => setBewerkVakantie(undefined)} />
      )}
    </div>
  );
}

// ── Hoofdpagina ──────────────────────────────────────────────────────────────

const TABS = [
  { key: 'medewerkers', label: 'Medewerkers' },
  { key: 'gemeenten', label: 'Gemeenten' },
  { key: 'verlof', label: 'Verlof & Ziekte' },
  { key: 'fte', label: 'FTE' },
  { key: 'vakanties', label: 'Vakanties', icon: Calendar },
  { key: 'logs', label: 'Logboek' },
  { key: 'wijzigingen', label: 'Wijzigingen' },
];

export default function Beheer() {
  const { resetRegels, importBackup, afwezigheid } = useRoosterStore();
  const [actieveTab, setActieveTab] = useState('medewerkers');
  const fileInputRef = useRef(null);

  function doReset() {
    if (!window.confirm('Alle aanpassingen in medewerkers en gemeenten resetten naar de standaardwaarden? Dit kan niet ongedaan worden gemaakt.')) return;
    resetRegels();
  }

  function doExport() {
    const ok = downloadBackup(useRoosterStore.getState());
    if (!ok) window.alert('Exporteren is mislukt. Probeer het opnieuw.');
  }

  function doImportKlik() {
    fileInputRef.current?.click();
  }

  async function doImportBestand(e) {
    const input = e.target;
    const file = input.files && input.files[0];
    input.value = ''; // meteen resetten zodat hetzelfde bestand opnieuw gekozen kan worden
    if (!file) return;

    let parsed;
    try {
      parsed = JSON.parse(await file.text());
    } catch {
      window.alert('Ongeldig back-up-bestand: kan het niet als JSON lezen.');
      return;
    }

    const { ok, fout, mismatch } = valideerBackup(parsed, leesActieveOmgeving());
    if (!ok) {
      window.alert(fout);
      return;
    }

    // Zit er een ANDERE omgeving in het bestand dan de actieve? Importeren schrijft naar de
    // actieve lade, dus dan overschrijf je de ene omgeving met de data van de andere. Mag,
    // maar niet per ongeluk: noem allebei de omgevingen bij naam in de bevestiging.
    if (!window.confirm(mismatch
      ? `Let op: dit bestand bevat de omgeving "${mismatch.bestand}", maar "${mismatch.actief}" is nu actief.\n\n`
        + `Importeren vervangt de data van "${mismatch.actief}" door die van "${mismatch.bestand}". Doorgaan?`
      : 'Dit vervangt alle huidige data met de back-up. Doorgaan?')) return;

    importBackup(parsed.data);
    window.alert('Back-up geimporteerd.');
  }

  const tabLabel = {
    verlof: `Verlof & Ziekte (${afwezigheid.length})`,
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-y-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Beheer</h1>
          <p className="text-sm text-slate-500 mt-1">Medewerkers, gemeenten, verlof en ziekte</p>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2">
          <button
            onClick={doExport}
            className="flex items-center gap-1.5 px-3 py-2 text-xs text-slate-500 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 hover:text-slate-700 transition"
            title="Download alle data als back-up-bestand"
          >
            <Download className="w-3.5 h-3.5" />
            Exporteer back-up
          </button>
          <button
            onClick={doImportKlik}
            className="flex items-center gap-1.5 px-3 py-2 text-xs text-slate-500 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 hover:text-slate-700 transition"
            title="Zet een eerder geexporteerde back-up terug"
          >
            <Upload className="w-3.5 h-3.5" />
            Importeer back-up
          </button>
          <button
            onClick={doReset}
            className="flex items-center gap-1.5 px-3 py-2 text-xs text-slate-500 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 hover:text-red-600 transition"
            title="Reset alle medewerkers en gemeenten naar standaard"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            Reset naar standaard
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="application/json"
            onChange={doImportBestand}
            className="hidden"
          />
        </div>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-1 bg-slate-100 rounded-xl p-1 w-fit max-w-full">
        {TABS.map(t => (
          <button
            key={t.key}
            onClick={() => setActieveTab(t.key)}
            className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition whitespace-nowrap shrink-0 ${
              actieveTab === t.key
                ? 'bg-white text-slate-800 shadow-sm'
                : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            {t.icon && <t.icon className="w-3.5 h-3.5" />}
            {tabLabel[t.key] || t.label}
          </button>
        ))}
      </div>

      {actieveTab === 'medewerkers' && <MedewerkersTab />}
      {actieveTab === 'gemeenten' && <GemeentenTab />}
      {actieveTab === 'verlof' && <VerlofTab />}
      {actieveTab === 'fte' && <FteTab />}
      {actieveTab === 'vakanties' && <VakantiesTab />}
      {actieveTab === 'logs' && <LogViewer />}
      {actieveTab === 'wijzigingen' && <WijzigingViewer />}
    </div>
  );
}
